import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material";
import {
  exchangeSteps as steps,
  extractStepRequestData,
  Status,
  statusIconMap,
  statusColorMap,
} from "../constants/ocpi.constant";
import { usePollingQuery, useMutate } from "src/hooks/useFetch.hook";
import { useParams } from "react-router-dom";
import { handshake } from "src/react-query/endPoints";

// Accept Formik props
const CredentialExchangeStep = ({ values, setFieldValue, isSubmitting }) => {
  const { id } = useParams();
  const theme = useTheme();
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [handshakeStarted, setHandshakeStarted] = useState(false);
  const [error, setError] = useState(null);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const { mutateAsync } = useMutate("handshake");

  // Safety mechanism: stop polling after 5 minutes to prevent infinite polling
  const MAX_POLLING_DURATION = 5 * 60 * 1000; // 5 minutes

  // Stable callback for data changes to prevent infinite re-renders
  const handleDataChange = useCallback((data) => {
    // Update form field when data changes
    if (data?.completed !== undefined) {
      setFieldValue("credentialExchangeStatus.completed", !!data.completed);
    }
  }, [setFieldValue]);

  // Polling query for handshake status
  const { data, isLoading, refetch } = usePollingQuery({
    enabled: true, // Always enabled to fetch initial status
    queryKey: "handshakeStatus",
    intervalMs: 2000, // Poll every 2 seconds
    initialFetch: true, // Fetch initially to get current status
    immediateStart: false, // Don't start polling immediately, only fetch once
    startPolling: pollingEnabled, // Only start continuous polling when enabled
    stopWhen: (data = {}) => {
      return data?.completed === true;
    },
    onDataChange: handleDataChange,
  });



  // Fetch initial status on page load
  useEffect(() => {
    // Fetch the current status when component mounts
    refetch();
  }, [refetch]);

  // Handle start button click
  const handleStart = async () => {
    if (data && data.completed) {
      return false;
    }

    if (isStarting) return; // Prevent multiple simultaneous calls

    try {
      setIsStarting(true);
      setError(null); // Clear any previous errors
      const extractedFormData = extractStepRequestData(values, 5);
      
      // Start polling immediately
      setPollingEnabled(true);
      setHandshakeStarted(true);
      setPollingStartTime(Date.now()); // Track when polling started
      
      // Make the handshake API call
      const result = await mutateAsync({
        method: "POST",
        body: extractedFormData,
        url: handshake(id),
      });

      // If API call fails, stop polling
      if (![1000, 200, 201].includes(result?.status || result?.statusCode)) {
        console.error("Handshake API failed with status:", result?.status || result?.statusCode);
        setPollingEnabled(false);
        setHandshakeStarted(false);
        setPollingStartTime(null);
        setError("Failed to start handshake process. Please try again.");
      }
    } catch (err) {
      console.error("Handshake error:", err);
      setPollingEnabled(false);
      setHandshakeStarted(false);
      setPollingStartTime(null);
      setError(err?.response?.data?.message || err?.message || "An error occurred while starting the handshake process.");
    } finally {
      setIsStarting(false);
    }
  };

  // Effect to handle data changes and update UI state
  useEffect(() => {
    if (data) {
      console.log("Handshake data received:", data);
      
      // Check if there's already an ongoing handshake on page load
      const isHandshakeInProgress = Object.values(data).some(
        (s) => s === Status.IN_PROGRESS
      );
      
      // If handshake is in progress and we haven't started polling yet, start polling
      if (isHandshakeInProgress && !pollingEnabled && !handshakeStarted) {
        console.log("Existing handshake in progress, starting polling");
        setPollingEnabled(true);
        setHandshakeStarted(true);
        setPollingStartTime(Date.now());
      }
      
      // Safety check: stop polling if it's been running too long
      if (pollingStartTime && (Date.now() - pollingStartTime) > MAX_POLLING_DURATION) {
        console.warn("Polling stopped due to maximum duration reached");
        setPollingEnabled(false);
        setHandshakeStarted(false);
        setPollingStartTime(null);
        setError("Polling stopped due to timeout. Please try again.");
        return;
      }

      // If handshake is completed, stop polling
      if (data.completed) {
        console.log("Handshake completed, stopping polling");
        setPollingEnabled(false);
        setHandshakeStarted(false);
        setPollingStartTime(null);
      }
    }
  }, [data, pollingEnabled, handshakeStarted, pollingStartTime]); // Add all dependencies

  // Cleanup effect to stop polling when component unmounts
  useEffect(() => {
    return () => {
      setPollingEnabled(false);
      setHandshakeStarted(false);
      setPollingStartTime(null);
    };
  }, []);

  // Manual stop function for safety
  const stopPolling = useCallback(() => {
    setPollingEnabled(false);
    setHandshakeStarted(false);
    setPollingStartTime(null);
    setError(null);
  }, []);

  // Calculate completion percentage
  const completed = useMemo(() => {
    if (!data) return 0;
    return Object.values(data).filter(
      (s) => s === Status.COMPLETED
    ).length;
  }, [data]);

  // Check if we should show the start button
  const shouldShowStartButton = !handshakeStarted || !pollingEnabled;
  const isCompleted = data?.completed === true;
  const isInitialLoading = isLoading && !data;
  const canStartHandshake = !isInitialLoading && !isCompleted && shouldShowStartButton;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography
        variant="h5"
        sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
      >
        <Icon icon="mdi:key-chain-variant" width={24} /> Credential Exchange
        Process
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Partner: {values.partnerName || "IN-eff"}
      </Typography>
      
      {/* Progress Bar */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <LinearProgress
          variant={handshakeStarted && !isCompleted ? "indeterminate" : "determinate"}
          value={completed * 25}
          sx={{ height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" sx={{ float: "right", mt: 0.5 }}>
          {handshakeStarted && !isCompleted 
            ? "Processing..." 
            : `${completed}/4 steps completed`}
        </Typography>
      </Box>

      {isInitialLoading && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
          <Typography variant="body2" color="info.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:loading" className="spin" width={16} />
            Loading current handshake status...
          </Typography>
        </Box>
      )}

      {!isInitialLoading && !data && !handshakeStarted && (
        <Box sx={{ mb: 2, mt: 3, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
          <Typography variant="body2" color="warning.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:alert" width={16} />
            No handshake data found. Click "Start Credential Exchange" to begin the process.
          </Typography>
        </Box>
      )}

      {handshakeStarted && !isCompleted && (
        <Box sx={{ mb: 2, mt: 3, p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
          <Typography variant="body2" color="info.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:information" width={16} />
            Credential exchange is in progress. Please wait while we establish the connection...
          </Typography>
        </Box>
      )}

      {isCompleted && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
          <Typography variant="body2" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:check-circle" width={16} />
            Credential exchange completed successfully! All steps have been processed.
          </Typography>
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" color="error.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:alert-circle" width={16} />
            {error}
          </Typography>
        </Box>
      )}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {steps.map((step, idx) => (
          <Grid item xs={12} sm={6} key={step.key}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                p: 0,
                height: "100%",
                border:
                  data && data[step.key] === Status.IN_PROGRESS
                    ? `1px solid ${theme.palette.primary.light}`
                    : "",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  height: "100%",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      mb: 0.2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {
                      <Icon
                        icon={
                          statusIconMap[data ? data[step.key] : Status.PENDING]
                            .icon
                        }
                        color={
                          theme.palette[
                            statusColorMap[
                              data ? data[step.key] : Status.PENDING
                            ].color
                          ].main
                        }
                        className={
                          data && data[step.key] === Status.IN_PROGRESS
                            ? "spin"
                            : ""
                        }
                        width={18}
                      />
                    }{" "}
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                <Chip
                  label={data ? data[step.key] : Status.PENDING}
                  color={
                    statusColorMap[data ? data[step.key] : Status.PENDING].color
                  }
                  size="small"
                  sx={{
                    ml: 2,
                    minWidth: 70,
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color:
                      data && data[step.key] === Status.COMPLETED
                        ? "#fff"
                        : undefined,
                    background:
                      data && data[step.key] === Status.PENDING.toLowerCase()
                        ? "#f4f4f4"
                        : undefined,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Action Button */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<Icon icon="mdi:key-chain-variant" width={22} />}
          sx={{
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 1,
            py: 1.2,
            color: "white",
            flex: 1,
          }}
          onClick={handleStart}
          disabled={!canStartHandshake || isStarting}
        >
          {isCompleted
            ? "Credential Exchange Complete"
            : isStarting
            ? "Starting..."
            : handshakeStarted
            ? "Credential Exchange in Progress..."
            : "Start Credential Exchange"}
        </Button>
        
        {handshakeStarted && !isCompleted && (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<Icon icon="mdi:refresh" width={22} />}
            onClick={() => refetch()}
            disabled={isLoading}
            sx={{
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 1,
              py: 1.2,
              minWidth: 'auto',
            }}
          >
            Refresh
          </Button>
        )}

        {handshakeStarted && !isCompleted && (
          <Button
            variant="outlined"
            color="warning"
            size="large"
            startIcon={<Icon icon="mdi:stop" width={22} />}
            onClick={stopPolling}
            sx={{
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 1,
              py: 1.2,
              minWidth: 'auto',
            }}
          >
            Stop
          </Button>
        )}

        {error && (
          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<Icon icon="mdi:reload" width={22} />}
            onClick={handleStart}
            sx={{
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 1,
              py: 1.2,
              minWidth: 'auto',
            }}
          >
            Retry
          </Button>
        )}
      </Box>

      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Debug: Initial Loading: {isInitialLoading ? 'YES' : 'NO'} | 
            Polling: {pollingEnabled ? 'ON' : 'OFF'} | 
            Started: {handshakeStarted ? 'YES' : 'NO'} | 
            Loading: {isLoading ? 'YES' : 'NO'} | 
            Starting: {isStarting ? 'YES' : 'NO'} |
            Has Data: {data ? 'YES' : 'NO'} |
            Time: {pollingStartTime ? `${Math.round((Date.now() - pollingStartTime) / 1000)}s` : 'N/A'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CredentialExchangeStep;
